import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { KundeService, Kunde } from '../../../services/kunde.service';
import { StandortService, Standort } from '../../../services/standort.service';
import { MitarbeiterService, TechnicianOption } from '../../../services/mitarbeiter.service';
import { ProjektaufgabeService } from '../../../services/projektaufgabe.service';

@Component({
  selector: 'app-create-kontrolltermin-dialog',
  templateUrl: './create-kontrolltermin-dialog.component.html',
  styleUrls: ['./create-kontrolltermin-dialog.component.scss']
})
export class CreateKontrollterminDialogComponent implements OnInit {
  form: FormGroup;
  kunden: Kunde[] = [];
  activeKunden: Kunde[] = []; // Only active kunden (status_id = 1)
  standorte: Standort[] = [];
  techniker: TechnicianOption[] = [];
  filteredStandorte: Standort[] = [];
  loading = false;

  // German month names (Austrian)
  monthNames = [
    'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  // Available months with year (current month + next 4 months)
  availableMonths: Array<{ month: number; year: number; label: string; value: string }> = [];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateKontrollterminDialogComponent>,
    private kundeService: KundeService,
    private standortService: StandortService,
    private mitarbeiterService: MitarbeiterService,
    private projektaufgabeService: ProjektaufgabeService
  ) {
    this.form = this.fb.group({
      kunde_id: ['', Validators.required],
      standort_id: ['', Validators.required],
      techniker_id: ['', Validators.required],
      durchfuehrungsmonat: ['', Validators.required]
    });

    // Filter standorte when kunde changes
    this.form.get('kunde_id')?.valueChanges.subscribe(kundeId => {
      if (kundeId) {
        this.loadStandorteForKunde(kundeId);
        // Reset standort selection when kunde changes
        this.form.patchValue({ standort_id: '' });
      } else {
        this.filteredStandorte = [];
      }
    });
  }

  ngOnInit(): void {
    this.initializeAvailableMonths();
    this.loadKunden();
    this.loadTechniker();
    this.loadStandorte();
  }

  /**
   * Initialize available months (current month + next 4 months)
   */
  initializeAvailableMonths(): void {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0-11
    const currentYear = today.getFullYear();
    
    this.availableMonths = [];
    
    // Add current month and next 4 months (5 months total)
    for (let i = 0; i < 5; i++) {
      const monthIndex = (currentMonth + i) % 12;
      const year = currentYear + Math.floor((currentMonth + i) / 12);
      const monthName = this.monthNames[monthIndex];
      
      this.availableMonths.push({
        month: monthIndex + 1, // 1-12 for API
        year: year,
        label: `${monthName} ${year}`,
        value: `${year}-${String(monthIndex + 1).padStart(2, '0')}` // Format: YYYY-MM
      });
    }
  }

  loadKunden(): void {
    // Load only active kunden (status_id = 1)
    this.kundeService.getKunden(1, 200, '', 'suchbegriff', 'asc', 1).subscribe({
      next: (response) => {
        this.kunden = response.data || [];
        // Filter to only active kunden (status_id = 1)
        this.activeKunden = this.kunden.filter(k => k.status_id === 1);
      },
      error: (error) => {
        console.error('Error loading kunden:', error);
      }
    });
  }

  loadStandorte(): void {
    this.standortService.getStandorte(1, 200).subscribe({
      next: (response) => {
        this.standorte = response.data || [];
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
      }
    });
  }

  loadStandorteForKunde(kundeId: string): void {
    // Filter standorte by kunde_id
    this.filteredStandorte = this.standorte.filter(s => s.kunde_id === kundeId);
  }

  loadTechniker(): void {
    // All active technicians, already sorted by nachname/vorname
    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        this.techniker = response.data || [];
      },
      error: (error) => {
        console.error('Error loading techniker:', error);
      }
    });
  }

  getTechnikerName(techniker: TechnicianOption): string {
    return techniker.display_name || '-';
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.form.valid) {
      const formValue = this.form.value;
      
      // durchfuehrungsmonat is now in format "YYYY-MM"
      const [year, month] = formValue.durchfuehrungsmonat.split('-').map(Number);
      
      // Format date directly to avoid timezone issues
      // Use the 15th of the month as a default (middle of month)
      const datum_durchfuerung = `${year}-${String(month).padStart(2, '0')}-15`;
      
      // Get month name in German
      const monat_plan = this.monthNames[month - 1];

      // Create a temporary projektaufgabe first (required for kontrolltermin)
      // Always einmalig (wiederkehrend = 0)
      const projektaufgabeData: any = {
        beschreibung: `Einmalige Kontrolle - ${monat_plan} ${year}`,
        wiederkehrend: 0, // Always einmalig
        periodizitaet: 1, // Default, not used for einmalig
        kunde_id: formValue.kunde_id,
        standort_id: formValue.standort_id,
        techniker_id: formValue.techniker_id,
        einsatztyp: 'kontrolle',
        is_active: 0 // Not active since it's just for single kontrolltermin
      };

      this.loading = true;
      
      // Create projektaufgabe first, then kontrolltermin
      this.projektaufgabeService.createProjektaufgabe(projektaufgabeData).subscribe({
        next: (projektaufgabe) => {
          // Now create kontrolltermin with the projektaufgabe_id
          const kontrollterminData: any = {
            kunde_id: formValue.kunde_id,
            standort_id: formValue.standort_id,
            techniker_id: formValue.techniker_id,
            projektaufgabe_id: projektaufgabe.id,
            einsatztyp: 'kontrolle',
            status_id: 4, // geplant
            datum_durchfuerung: datum_durchfuerung,
            monat_plan: monat_plan
          };

          this.dialogRef.close({ event: 'create', data: kontrollterminData });
        },
        error: (error) => {
          console.error('Error creating projektaufgabe:', error);
          this.loading = false;
          // Close dialog with error
          this.dialogRef.close({ event: 'error', error: error });
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
    }
  }
}

